// File: frontend/src/pages/Quote/components/Hero/Hero.js
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import styles from './Hero.module.css';

const Hero = ({ onStartQuote }) => {
  const { t } = useLanguage();
  const canvasRef = useRef(null);
  const [isAnimated, setIsAnimated] = useState(false);
  const hasAnimatedRef = useRef(false);
  const animationFrameRef = useRef(null);
  const isVisibleRef = useRef(true);

  // Pull your primary font name from :root
  const primaryFont = (
    getComputedStyle(document.documentElement)
      .getPropertyValue('--font-primary')
      .trim() || "'Instrument Sans', sans-serif"
  );

  // Draw the fully rendered, static version
  const drawFinalState = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const width  = canvas.width  / dpr;
    const height = canvas.height / dpr;
    // Move centerY up by 10% to make room for labels
    const centerY = (height / 2) - (height * 0.1);
    const radius  = Math.min(Math.min(width, height) / 4, 100) * 1.3;

    const traditionalX = width / 4;
    const allegoryX    = (width / 4) * 3;

    // Base circles
    [[traditionalX, centerY], [allegoryX, centerY]].forEach(([x, y]) => {
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, 2 * Math.PI);
      ctx.fillStyle = 'rgba(255,255,255,0.1)';
      ctx.fill();
    });

    // Expense slices
    ctx.beginPath();
    ctx.moveTo(traditionalX, centerY);
    ctx.arc(traditionalX, centerY, radius,
            -0.5 * Math.PI,
            -0.5 * Math.PI + 0.33 * 2 * Math.PI);
    ctx.closePath();
    ctx.fillStyle = '#FF6B6B';
    ctx.fill();

    ctx.beginPath();
    ctx.moveTo(allegoryX, centerY);
    ctx.arc(allegoryX, centerY, radius,
            -0.5 * Math.PI,
            -0.5 * Math.PI + 0.0833 * 2 * Math.PI);
    ctx.closePath();
    ctx.fillStyle = '#38C793';
    ctx.fill();

    // Slightly smaller labels
    const baseFontSize    = Math.max(24, Math.min(32, width  / 26));
    const expenseFontSize = Math.max(18, Math.min(24, width  / 35));

    ctx.textAlign = 'center';

    ctx.fillStyle = 'rgba(255,255,255,1)';
    ctx.font      = `600 ${baseFontSize}px ${primaryFont}`;
    ctx.fillText(t('calculator.traditional'),
                 traditionalX, centerY + radius + 40);
    ctx.fillText(t('hero.allegory'),
                 allegoryX,     centerY + radius + 40);

    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.font      = `500 ${expenseFontSize}px ${primaryFont}`;
    ctx.fillText(
      t('hero.expenses_percentage', { percentage: 33 }),
      traditionalX, centerY + radius + 75
    );
    ctx.fillText(
      t('hero.expenses_percentage', { percentage: 8 }),
      allegoryX,    centerY + radius + 75
    );

    // Divider
    ctx.beginPath();
    ctx.moveTo(width / 2, 30);
    ctx.lineTo(width / 2, height - 30);
    ctx.strokeStyle = 'rgba(255,255,255,0.2)';
    ctx.setLineDash([5, 3]);
    ctx.stroke();
    ctx.setLineDash([]);

    // Arrow
    const arrowOffsetX = Math.min(90, radius + 10) * 1.3;
    const arrowHeadSize = Math.min(14, radius / 6);
    ctx.beginPath();
    ctx.moveTo(traditionalX + arrowOffsetX, centerY);
    ctx.lineTo(allegoryX - arrowOffsetX,     centerY);
    ctx.strokeStyle = 'rgba(56,199,147,1)';
    ctx.lineWidth   = 3;
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(allegoryX - arrowOffsetX,     centerY);
    ctx.lineTo(allegoryX - arrowOffsetX - arrowHeadSize, centerY - arrowHeadSize);
    ctx.lineTo(allegoryX - arrowOffsetX - arrowHeadSize, centerY + arrowHeadSize);
    ctx.closePath();
    ctx.fillStyle = 'rgba(56,199,147,1)';
    ctx.fill();
  }, [t, primaryFont]);

  // Animation & responsiveness
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    // Resize for high DPI
    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width  = rect.width  * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
    };
    resizeCanvas();

    // Compute circle & positions
    const getRadius = () =>
      Math.min(Math.min(canvas.width / dpr, canvas.height / dpr) / 4, 100) * 1.3;

    const getPositions = () => {
      const r  = getRadius();
      // Move centerY up by 10% to make room for labels
      const cy = (canvas.height / (2 * dpr)) - ((canvas.height / dpr) * 0.1);
      return {
        traditional: { x: canvas.width / (4 * dpr), y: cy, radius: r, color: '#FF6B6B' },
        allegory:    { x: (canvas.width / (4 * dpr)) * 3, y: cy, radius: r, color: '#38C793' }
      };
    };
    const { traditional, allegory } = getPositions();

    // Draw static bases + labels
    const drawBase = () => {
      ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
      [traditional, allegory].forEach(c => {
        ctx.beginPath();
        ctx.arc(c.x, c.y, c.radius, 0, 2 * Math.PI);
        ctx.fillStyle = 'rgba(255,255,255,0.1)';
        ctx.fill();
      });

      const isDesktop = window.innerWidth > 1024;
      // Slightly smaller font size
      const baseFontSize = Math.max(
        24,
        Math.min(isDesktop ? 32 : 28, canvas.width / (isDesktop ? 26 : 30) / dpr)
      );
      ctx.textAlign = 'center';
      ctx.fillStyle = 'rgba(255,255,255,1)';
      ctx.font      = `600 ${baseFontSize}px ${primaryFont}`;
      ctx.fillText(t('calculator.traditional'),
                   traditional.x, traditional.y + traditional.radius + 40);
      ctx.fillText(t('calculator.allegory'),
                   allegory.x,    allegory.y    + allegory.radius    + 40);
    };

    // Draw slices + labels + divider + arrow based on progress
    const drawSlices = prog => {
      const isDesktop = window.innerWidth > 1024;
      // Slightly smaller font size
      const expenseFontSize = Math.max(
        18,
        Math.min(isDesktop ? 24 : 22, canvas.width / (isDesktop ? 35 : 40) / dpr)
      );

      const angleT = prog * (0.33 * 2 * Math.PI);
      const angleA = prog * (0.0833 * 2 * Math.PI);

      [[traditional, angleT], [allegory, angleA]].forEach(([c, angle]) => {
        ctx.beginPath();
        ctx.moveTo(c.x, c.y);
        ctx.arc(c.x, c.y, c.radius, -0.5 * Math.PI, -0.5 * Math.PI + angle);
        ctx.closePath();
        ctx.fillStyle = c.color;
        ctx.fill();
      });

      ctx.textAlign = 'center';
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.font      = `500 ${expenseFontSize}px ${primaryFont}`;
      ctx.fillText(
        t('hero.expenses_percentage', { percentage: Math.round(prog * 33) }),
        traditional.x, traditional.y + traditional.radius + 75
      );
      ctx.fillText(
        t('hero.expenses_percentage', { percentage: Math.round(prog * 8) }),
        allegory.x,    allegory.y    + allegory.radius    + 75
      );

      if (prog > 0.3) {
        const a = ((prog - 0.3) / 0.7) * 0.2;
        ctx.beginPath();
        ctx.moveTo(canvas.width / (2 * dpr), 30 / dpr);
        ctx.lineTo(canvas.width / (2 * dpr), (canvas.height - 30) / dpr);
        ctx.strokeStyle = `rgba(255,255,255,${a})`;
        ctx.setLineDash([5, 3]);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      if (prog > 0.6) {
        const a = (prog - 0.6) / 0.4;
        const arrowOffsetX = Math.min(90, traditional.radius + 10) * 1.3;
        const arrowHeadSize = Math.min(14, traditional.radius / 6);

        ctx.beginPath();
        ctx.moveTo(traditional.x + arrowOffsetX, traditional.y);
        ctx.lineTo(allegory.x - arrowOffsetX, traditional.y);
        ctx.strokeStyle = `rgba(56,199,147,${a})`;
        ctx.lineWidth   = 3;
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(allegory.x - arrowOffsetX, traditional.y);
        ctx.lineTo(allegory.x - arrowOffsetX - arrowHeadSize, traditional.y - arrowHeadSize);
        ctx.lineTo(allegory.x - arrowOffsetX - arrowHeadSize, traditional.y + arrowHeadSize);
        ctx.closePath();
        ctx.fillStyle = `rgba(56,199,147,${a})`;
        ctx.fill();
      }
    };

    let start = null;
    const duration = 1500;
    const step = ts => {
      if (!start) start = ts;
      const prog = Math.min((ts - start) / duration, 1);
      drawBase();
      drawSlices(prog);
      if (prog < 1) {
        animationFrameRef.current = requestAnimationFrame(step);
      } else {
        setIsAnimated(true);
        hasAnimatedRef.current = true;
      }
    };

    // Kick off animation or final draw
    if (!hasAnimatedRef.current) {
      drawBase();
      animationFrameRef.current = requestAnimationFrame(step);
    } else {
      drawFinalState();
    }

    // Handle canvas resize
    const onResize = () => {
      resizeCanvas();
      if (hasAnimatedRef.current) drawFinalState();
    };
    window.addEventListener('resize', onResize);

    // Cleanup
    return () => {
      cancelAnimationFrame(animationFrameRef.current);
      window.removeEventListener('resize', onResize);
    };
  }, [t, drawFinalState, primaryFont]);

  return (
    <div className={styles.heroContainer}>
      <div className={styles.backgroundShapes}>
        <div className={styles.shape1} />
        <div className={styles.shape2} />
      </div>
      <div className={styles.heroContent}>
        <h1 className={styles.title}>
          {t('hero.title.line1')}<br />
          {/* {t('hero.title.line2_part1')} */}
          <span className={styles.highlight}>
            {t('hero.title.line2_part2')}
          </span>
        </h1>
        <p className={styles.subtitle}>{t('hero.subtitle')}</p>
        <button
          className={styles.ctaButton}
          onClick={onStartQuote}
          aria-label={t('hero.cta_button')}
        >
          {t('hero.cta_button')} <span className={styles.arrow}>→</span>
        </button>
      </div>
      <div className={styles.visualSection}>
        <canvas
          ref={canvasRef}
          className={styles.comparisonCanvas}
          aria-hidden="true"
        />
        <div className={styles.savingsBadge} aria-hidden="true">
          <div className={styles.badgeContent}>
            <span className={styles.savingsText}>{t('hero.saving_percentage')}</span>
            <span className={styles.savingsLabel}>{t('hero.saving_all_caps')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;